import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  Account,
  Trade,
  Strategy,
  Setup,
  Language,
  ModalState,
  PropExpense,
  PropPayout,
  BudgetSettings,
  CalendarPrefs,
} from './types';
import { DEFAULT_CALENDAR_PREFS } from './types';
import { redeemCoupon } from './lib/coupon';
import {
  MOCK_ACCOUNTS,
  MOCK_TRADES,
  MOCK_STRATEGIES,
  MOCK_EXPENSES,
  MOCK_PAYOUTS,
  MOCK_SETUPS,
  TURTLE_SOUP_CHECKBOXES,
} from './mockData';
import {
  loadUserData,
  dbSaveAccount,
  dbDeleteAccount,
  dbSaveTrade,
  dbSaveTradeMfe,
  dbDeleteTrade,
  dbSaveStrategy,
  dbDeleteStrategy,
  dbSeedNewUser,
  dbSaveProfile,
  dbSaveExpense,
  dbDeleteExpense,
  dbSavePayout,
  dbDeletePayout,
  dbSaveBudgetSettings,
  dbSaveTimezone,
  dbSaveBreakEvenRange,
  dbSaveCalendarPrefs,
  dbSaveLanguage,
  dbSaveSignupConsent,
  CONSENT_LOCAL_KEY,
  loadViewingData,
  dbBindMentorLinks,
  dbListStudents,
} from './lib/db';
import { splitByOutcome } from './lib/classify';
import { buildCheckoutUrl } from './lib/checkout';

/**
 * Write the consent stashed by the Auth screen (survives the OAuth redirect /
 * email-confirm round-trip) to the new user's profile, then clear it. No-op when
 * the user didn't pass through our signup screen (e.g. session restored silently).
 */
function flushSignupConsent(userId: string) {
  try {
    const raw = localStorage.getItem('signup_consent');
    if (!raw) return;
    const c = JSON.parse(raw) as { version?: string; marketing?: boolean };
    const version = c.version ?? 'unknown';
    dbSaveSignupConsent(userId, { version, marketing: c.marketing === true });
    localStorage.setItem(CONSENT_LOCAL_KEY, version); // skip the gate on this device
    localStorage.removeItem('signup_consent');
  } catch {
    /* ignore malformed/unavailable storage */
  }
}

function checkAndRedirectToWhop(
  email: string,
  userId: string,
  subscriptionStatus: 'active' | 'trialing' | 'expired' | null,
) {
  const authNext = localStorage.getItem('auth_next');
  if (authNext !== 'subscribe') return;
  localStorage.removeItem('auth_next');
  if (subscriptionStatus === 'expired') {
    window.location.href = buildCheckoutUrl({ email, userId });
  }
}

export function createDefaultStrategies(): Strategy[] {
  const stratId = crypto.randomUUID();
  return [
    {
      id: stratId,
      name: 'Turtle Soup',
      description: 'ICT Turtle Soup Setup',
      color: '#635bff',
      is_active: true,
      fields: [
        ...TURTLE_SOUP_CHECKBOXES.map((label, i) => ({
          id: crypto.randomUUID(),
          strategy_id: stratId,
          field_type: 'checkbox' as const,
          label,
          is_required: false,
          sort_order: i + 1,
        })),
        {
          id: crypto.randomUUID(),
          strategy_id: stratId,
          field_type: 'text' as const,
          label: 'HTF PD Array',
          placeholder: 'e.g. 4H FVG / 1D OB',
          is_required: false,
          sort_order: 11,
        },
        {
          id: crypto.randomUUID(),
          strategy_id: stratId,
          field_type: 'text' as const,
          label: 'Psychology',
          placeholder: 'Focus, emotions...',
          is_required: false,
          sort_order: 12,
        },
      ],
    },
  ];
}

export function createDefaultAccount(): Account {
  return {
    id: crypto.randomUUID(),
    name: 'חשבון אישי',
    account_type: 'personal',
    broker: 'manual',
    initial_balance: 0,
    currency: 'USD',
    is_active: true,
  };
}

interface AppState {
  isDemo: boolean;
  lastUserId: string | null;
  user: { id: string; email?: string; name?: string } | null;
  accounts: Account[];
  trades: Trade[];
  strategies: Strategy[];
  expenses: PropExpense[];
  payouts: PropPayout[];
  lang: Language;
  selectedAccountIds: string[];
  currentYear: number;
  currentMonth: number;
  modal: ModalState | null;
  activeView: string;
  fontSize: number;
  highContrast: boolean;
  grayscale: boolean;
  readableFont: boolean;
  sidebarCollapsed: boolean;
  darkMode: boolean;
  dataLoading: boolean;
  dailyGoalTarget: number;
  dailyMaxLoss: number;
  breakEvenRange: number;
  subscriptionStatus: 'active' | 'trialing' | 'expired' | null;
  trialEndsAt: string | null;
  budgetSettings: BudgetSettings | null;
  calendarPrefs: CalendarPrefs;
  setCalendarPrefs: (p: Partial<CalendarPrefs>) => void;
  timezone: string;
  analyticsRange: 'week' | 'month' | 'all' | 'custom';
  customRange: { start: string; end: string } | null;
  liveBalances: Record<string, number>;
  setLiveBalances: (map: Record<string, number>) => void;
  onboardingDone: boolean;
  setOnboardingDone: (v?: boolean) => void;
  setBudgetSettings: (s: BudgetSettings | null) => void;
  setTimezone: (tz: string) => void;
  setAnalyticsRange: (r: 'week' | 'month' | 'all' | 'custom') => void;
  setCustomRange: (r: { start: string; end: string } | null) => void;

  setDemo: (v: boolean) => void;
  setUser: (u: AppState['user']) => void;
  setLang: (l: Language) => void;
  setSelectedAccount: (id: string) => void;
  setSelectedAccountIds: (ids: string[]) => void;
  toggleSelectedAccount: (id: string) => void;
  isAccountSelected: (id: string) => boolean;
  setCurrentDate: (year: number, month: number) => void;
  setModal: (m: ModalState | null) => void;
  setActiveView: (v: string) => void;
  setSidebarCollapsed: (v: boolean) => void;
  setDarkMode: (v: boolean) => void;
  setDailyGoalTarget: (v: number) => void;
  setDailyMaxLoss: (v: number) => void;
  setBreakEvenRange: (v: number) => void;

  addTrade: (t: Trade) => void;
  updateTrade: (t: Trade) => void;
  updateTradeMfe: (id: string, mfeDollars: number) => void;
  deleteTrade: (id: string) => void;

  addAccount: (a: Account) => void;
  updateAccount: (a: Account) => void;
  setAccountArchived: (id: string, archived: boolean) => void;
  deleteAccount: (id: string) => void;

  addStrategy: (s: Strategy) => void;
  updateStrategy: (s: Strategy) => void;
  deleteStrategy: (id: string) => void;

  setups: Setup[];
  addSetup: (s: Setup) => void;
  updateSetup: (s: Setup) => void;
  deleteSetup: (id: string) => void;

  addExpense: (e: PropExpense) => void;
  updateExpense: (e: PropExpense) => void;
  deleteExpense: (id: string) => void;

  addPayout: (p: PropPayout) => void;
  updatePayout: (p: PropPayout) => void;
  deletePayout: (id: string) => void;

  setFontSize: (v: number) => void;
  setHighContrast: (v: boolean) => void;
  setGrayscale: (v: boolean) => void;
  setReadableFont: (v: boolean) => void;

  // Mentor read-only viewing
  viewingAs: { id: string; name: string } | null;
  isMentor: boolean;
  enterViewingMode: (studentId: string, name: string) => Promise<void>;
  exitViewingMode: () => Promise<void>;

  loadDemoData: () => void;
  initRealUser: (userId: string, name: string, email: string) => Promise<void>;
  loadDataInBackground: (userId: string, name: string, email: string) => void;
  reloadFromCloud: () => Promise<void>;
  getFilteredTrades: () => Trade[];
  getStats: () => {
    totalPnL: number;
    winRate: number;
    totalTrades: number;
    avgWin: number;
    avgLoss: number;
    profitFactor: number;
  };
}

const now = new Date();

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      isDemo: true,
      lastUserId: null,
      user: null,
      accounts: [],
      trades: [],
      strategies: [],
      setups: [],
      expenses: [],
      payouts: [],
      lang: 'en',
      selectedAccountIds: [],
      currentYear: now.getFullYear(),
      currentMonth: now.getMonth(),
      modal: null,
      activeView: 'calendar',
      fontSize: 100,
      highContrast: false,
      grayscale: false,
      readableFont: false,
      sidebarCollapsed: false,
      darkMode: false,
      dataLoading: false,
      dailyGoalTarget: 0,
      dailyMaxLoss: 0,
      breakEvenRange: 0,
      subscriptionStatus: null,
      trialEndsAt: null,
      viewingAs: null,
      isMentor: false,
      budgetSettings: null,
      calendarPrefs: DEFAULT_CALENDAR_PREFS,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      analyticsRange: 'month',
      customRange: null,
      liveBalances: {},
      setLiveBalances: (map) => set((s) => ({ liveBalances: { ...s.liveBalances, ...map } })),
      onboardingDone: false,
      setOnboardingDone: (v = true) => set({ onboardingDone: v }),
      setBudgetSettings: (s) => {
        set({ budgetSettings: s });
        const userId = get().user?.id;
        if (userId) dbSaveBudgetSettings(userId, s);
      },
      setCalendarPrefs: (p) => {
        const next = { ...get().calendarPrefs, ...p };
        set({ calendarPrefs: next });
        const userId = get().user?.id;
        if (userId) dbSaveCalendarPrefs(userId, next);
      },
      setTimezone: (tz) => {
        if (!tz) return;
        set({ timezone: tz });
        const userId = get().user?.id;
        if (userId) dbSaveTimezone(userId, tz);
      },
      setAnalyticsRange: (r) => set({ analyticsRange: r }),
      setCustomRange: (r) => set({ customRange: r, analyticsRange: r ? 'custom' : 'month' }),

      setDemo: (v) => set({ isDemo: v }),
      setUser: (u) => set({ user: u }),
      setLang: (l) => {
        set({ lang: l });
        const userId = get().user?.id;
        if (userId) dbSaveLanguage(userId, l);
      },
      setSelectedAccount: (id) => set({ selectedAccountIds: id === 'all' ? [] : [id] }),
      setSelectedAccountIds: (ids) => set({ selectedAccountIds: ids }),
      toggleSelectedAccount: (id) =>
        set((s) => {
          const has = s.selectedAccountIds.includes(id);
          return {
            selectedAccountIds: has
              ? s.selectedAccountIds.filter((x) => x !== id)
              : [...s.selectedAccountIds, id],
          };
        }),
      isAccountSelected: (id) => {
        const ids = get().selectedAccountIds;
        return ids.length === 0 || ids.includes(id);
      },
      setCurrentDate: (year, month) => set({ currentYear: year, currentMonth: month }),
      setModal: (m) => set({ modal: m }),
      setActiveView: (v) => set({ activeView: v }),
      setSidebarCollapsed: (v) => set({ sidebarCollapsed: v }),
      setDarkMode: (v) => set({ darkMode: v }),
      setDailyGoalTarget: (v) => {
        if (get().viewingAs) return;
        set({ dailyGoalTarget: v });
        const userId = get().user?.id;
        if (userId) dbSaveProfile(userId, { dailyGoalTarget: v, dailyMaxLoss: get().dailyMaxLoss });
      },
      setDailyMaxLoss: (v) => {
        if (get().viewingAs) return;
        set({ dailyMaxLoss: v });
        const userId = get().user?.id;
        if (userId)
          dbSaveProfile(userId, { dailyGoalTarget: get().dailyGoalTarget, dailyMaxLoss: v });
      },
      setBreakEvenRange: (v) => {
        if (get().viewingAs) return;
        set({ breakEvenRange: v });
        const userId = get().user?.id;
        if (userId) dbSaveBreakEvenRange(userId, v);
      },

      // ── עסקאות — שמור גם ב-Supabase ──
      addTrade: (t) => {
        if (get().viewingAs) return;
        set((s) => ({ trades: [t, ...s.trades] }));
        const userId = get().user?.id;
        if (userId) dbSaveTrade(t, userId);
      },
      updateTrade: (t) => {
        if (get().viewingAs) return;
        set((s) => ({ trades: s.trades.map((x) => (x.id === t.id ? t : x)) }));
        const userId = get().user?.id;
        if (userId) dbSaveTrade(t, userId);
      },
      updateTradeMfe: (id, mfeDollars) => {
        if (get().viewingAs) return;
        set((s) => ({
          trades: s.trades.map((x) => (x.id === id ? { ...x, mfe_dollars: mfeDollars } : x)),
        }));
        const userId = get().user?.id;
        if (userId) dbSaveTradeMfe(id, mfeDollars, userId);
      },
      deleteTrade: (id) => {
        if (get().viewingAs) return;
        set((s) => ({ trades: s.trades.filter((x) => x.id !== id) }));
        dbDeleteTrade(id);
      },

      // ── חשבונות — שמור גם ב-Supabase ──
      addAccount: (a) => {
        if (get().viewingAs) return;
        set((s) => ({ accounts: [...s.accounts, a] }));
        const userId = get().user?.id;
        if (userId) dbSaveAccount(a, userId).catch((e) => console.error('[DB] addAccount:', e));
      },
      updateAccount: (a) => {
        if (get().viewingAs) return;
        set((s) => ({ accounts: s.accounts.map((x) => (x.id === a.id ? a : x)) }));
        const userId = get().user?.id;
        if (userId) dbSaveAccount(a, userId).catch((e) => console.error('[DB] updateAccount:', e));
      },
      setAccountArchived: (id, archived) => {
        if (get().viewingAs) return;
        const acc = get().accounts.find((x) => x.id === id);
        if (!acc || acc.is_active === !archived) return;
        const updated = { ...acc, is_active: !archived };
        set((s) => ({
          accounts: s.accounts.map((x) => (x.id === id ? updated : x)),
          // Archiving deselects the account from the active filter
          selectedAccountIds: archived
            ? s.selectedAccountIds.filter((x) => x !== id)
            : s.selectedAccountIds,
        }));
        const userId = get().user?.id;
        if (userId)
          dbSaveAccount(updated, userId).catch((e) => console.error('[DB] setAccountArchived:', e));
      },
      deleteAccount: (id) => {
        if (get().viewingAs) return;
        set((s) => ({
          accounts: s.accounts.filter((x) => x.id !== id),
          trades: s.trades.filter((x) => x.account_id !== id),
          selectedAccountIds: s.selectedAccountIds.filter((x) => x !== id),
        }));
        dbDeleteAccount(id);
      },

      // ── הוצאות ותשלומים — שמור גם ב-Supabase ──
      addExpense: (e: PropExpense) => {
        if (get().viewingAs) return;
        set((st: AppState) => ({ expenses: [e, ...st.expenses] }));
        const userId = get().user?.id;
        if (userId) dbSaveExpense(e, userId);
      },
      updateExpense: (e: PropExpense) => {
        if (get().viewingAs) return;
        set((st: AppState) => ({
          expenses: st.expenses.map((x: PropExpense) => (x.id === e.id ? e : x)),
        }));
        const userId = get().user?.id;
        if (userId) dbSaveExpense(e, userId);
      },
      deleteExpense: (id: string) => {
        if (get().viewingAs) return;
        set((st: AppState) => ({ expenses: st.expenses.filter((x: PropExpense) => x.id !== id) }));
        dbDeleteExpense(id);
      },

      addPayout: (p: PropPayout) => {
        if (get().viewingAs) return;
        set((st: AppState) => ({ payouts: [p, ...st.payouts] }));
        const userId = get().user?.id;
        if (userId) dbSavePayout(p, userId);
      },
      updatePayout: (p: PropPayout) => {
        if (get().viewingAs) return;
        set((st: AppState) => ({
          payouts: st.payouts.map((x: PropPayout) => (x.id === p.id ? p : x)),
        }));
        const userId = get().user?.id;
        if (userId) dbSavePayout(p, userId);
      },
      deletePayout: (id: string) => {
        if (get().viewingAs) return;
        set((st: AppState) => ({ payouts: st.payouts.filter((x: PropPayout) => x.id !== id) }));
        dbDeletePayout(id);
      },

      // ── אסטרטגיות — שמור גם ב-Supabase ──
      addStrategy: (s) => {
        if (get().viewingAs) return;
        set((st) => ({ strategies: [...st.strategies, s] }));
        const userId = get().user?.id;
        if (userId) dbSaveStrategy(s, userId);
      },
      updateStrategy: (s) => {
        if (get().viewingAs) return;
        set((st) => ({ strategies: st.strategies.map((x) => (x.id === s.id ? s : x)) }));
        const userId = get().user?.id;
        if (userId) dbSaveStrategy(s, userId);
      },
      deleteStrategy: (id) => {
        if (get().viewingAs) return;
        set((st) => ({ strategies: st.strategies.filter((x) => x.id !== id) }));
        dbDeleteStrategy(id);
      },

      // ── Setups ──
      addSetup: (s) => {
        if (get().viewingAs) return;
        set((st) => ({ setups: [...st.setups, s] }));
      },
      updateSetup: (s) => {
        if (get().viewingAs) return;
        set((st) => ({ setups: st.setups.map((x) => (x.id === s.id ? s : x)) }));
      },
      deleteSetup: (id) => {
        if (get().viewingAs) return;
        set((st) => ({ setups: st.setups.filter((x) => x.id !== id) }));
      },

      setFontSize: (v) => set({ fontSize: v }),
      setHighContrast: (v) => set({ highContrast: v }),
      setGrayscale: (v) => set({ grayscale: v }),
      setReadableFont: (v) => set({ readableFont: v }),

      loadDemoData: () =>
        set({
          accounts: MOCK_ACCOUNTS,
          trades: MOCK_TRADES.map((t) => ({
            ...t,
            setup_id:
              t.strategy_id === 's1'
                ? 'pb1'
                : t.strategy_id === 's2'
                  ? 'pb2'
                  : t.strategy_id === 's3'
                    ? 'pb3'
                    : undefined,
          })),
          strategies: MOCK_STRATEGIES,
          expenses: MOCK_EXPENSES,
          payouts: MOCK_PAYOUTS,
          setups: MOCK_SETUPS,
          isDemo: true,
          lastUserId: null,
          user: { id: 'demo', name: 'Demo User', email: 'demo@tradelog.app' },
        }),

      // ── התחברות משתמש אמיתי — טעינה מ-Supabase ──
      initRealUser: async (userId, name, email) => {
        set({ dataLoading: true, isDemo: false, user: { id: userId, name, email } });

        // timeout — אם Supabase לא מגיב תוך 8 שניות, פתח בכל זאת
        const timeout = setTimeout(() => {
          console.warn('loadUserData timeout — opening with empty state');
          set({ dataLoading: false });
        }, 8000);

        try {
          const {
            accounts,
            strategies,
            trades,
            expenses,
            payouts,
            setups,
            dailyGoalTarget,
            dailyMaxLoss,
            breakEvenRange,
            subscriptionStatus,
            trialEndsAt,
            budgetSettings,
            calendarPrefs,
          } = await loadUserData(userId);
          clearTimeout(timeout);
          const isNewUser = accounts.length === 0 && strategies.length === 0;

          if (isNewUser) {
            const defAccount = createDefaultAccount();
            const defStrategies = createDefaultStrategies();
            await dbSeedNewUser(userId, defAccount, defStrategies);
            flushSignupConsent(userId);
            set({
              accounts: [defAccount],
              strategies: defStrategies,
              setups: [],
              trades: [],
              expenses: [],
              payouts: [],
              lastUserId: userId,
              selectedAccountIds: [],
              subscriptionStatus,
              trialEndsAt,
              budgetSettings,
            });
          } else {
            const selectedAccountIds = get().selectedAccountIds.filter((id) =>
              accounts.some((a) => a.id === id),
            );
            set({
              accounts,
              strategies,
              setups,
              trades,
              expenses,
              payouts,
              dailyGoalTarget,
              dailyMaxLoss,
              breakEvenRange,
              lastUserId: userId,
              selectedAccountIds,
              subscriptionStatus,
              trialEndsAt,
              budgetSettings,
              calendarPrefs,
            });
          }
        } catch (err) {
          clearTimeout(timeout);
          console.error('initRealUser error:', err);
          const defAccount = createDefaultAccount();
          const defStrategies = createDefaultStrategies();
          set({
            accounts: [defAccount],
            strategies: defStrategies,
            setups: [],
            trades: [],
            expenses: [],
            payouts: [],
            lastUserId: userId,
            selectedAccountIds: [],
          });
        } finally {
          set({ dataLoading: false });
        }
      },

      // טעינת נתונים ברקע — לא חוסם את הממשק
      loadDataInBackground: (userId, name, email) => {
        set({ user: { id: userId, name, email }, isDemo: false });
        loadUserData(userId)
          .then(
            ({
              accounts,
              strategies,
              trades,
              expenses,
              payouts,
              setups,
              dailyGoalTarget,
              dailyMaxLoss,
              breakEvenRange,
              subscriptionStatus,
              trialEndsAt,
              budgetSettings,
              calendarPrefs,
              timezone,
              timezoneFromDb,
            }) => {
              const isKnownUser = get().lastUserId === userId;
              const isNewUser = !isKnownUser && accounts.length === 0 && strategies.length === 0;

              if (isNewUser) {
                const defAccount = createDefaultAccount();
                const defStrategies = createDefaultStrategies();
                dbSeedNewUser(userId, defAccount, defStrategies);
                flushSignupConsent(userId);
                set({
                  accounts: [defAccount],
                  strategies: defStrategies,
                  setups: [],
                  trades: [],
                  expenses: [],
                  payouts: [],
                  lastUserId: userId,
                  subscriptionStatus,
                  trialEndsAt,
                  budgetSettings,
                  timezone,
                });
              } else if (accounts.length > 0) {
                const selectedAccountIds = get().selectedAccountIds.filter((id) =>
                  accounts.some((a) => a.id === id),
                );
                set({
                  accounts,
                  strategies,
                  setups,
                  trades,
                  expenses,
                  payouts,
                  dailyGoalTarget,
                  dailyMaxLoss,
                  breakEvenRange,
                  lastUserId: userId,
                  selectedAccountIds,
                  subscriptionStatus,
                  trialEndsAt,
                  budgetSettings,
                  calendarPrefs,
                  timezone,
                });
              } else {
                console.warn('[Store] Known user returned 0 accounts — keeping cache');
                set({ subscriptionStatus, trialEndsAt, budgetSettings });
              }
              checkAndRedirectToWhop(email, userId, subscriptionStatus);
              // Pending coupon stashed by the /redeem deep link before login.
              const pendingCoupon = localStorage.getItem('redeem_code');
              if (pendingCoupon) {
                localStorage.removeItem('redeem_code');
                redeemCoupon(pendingCoupon)
                  .then((r) => {
                    if (r.ok) set({ subscriptionStatus: 'active' });
                  })
                  .catch(() => {});
              }
              // Bind any mentor invites addressed to this email, then flag mentor status.
              dbBindMentorLinks()
                .then(() => dbListStudents())
                .then((studs) => set({ isMentor: studs.length > 0 }))
                .catch(() => {});
              if (!timezoneFromDb) {
                dbSaveTimezone(userId, timezone);
              }
            },
          )
          .catch((err) => {
            console.warn('[Store] Background load failed, using cache:', err);
          });
      },

      reloadFromCloud: async () => {
        const userId = get().user?.id;
        if (!userId) return;
        set({ dataLoading: true });
        try {
          const {
            accounts,
            strategies,
            trades,
            expenses,
            payouts,
            setups,
            dailyGoalTarget,
            dailyMaxLoss,
            breakEvenRange,
            subscriptionStatus,
            trialEndsAt,
            budgetSettings,
            calendarPrefs,
            timezone,
          } = await loadUserData(userId);
          if (accounts.length > 0) {
            const selectedAccountIds = get().selectedAccountIds.filter((id) =>
              accounts.some((a) => a.id === id),
            );
            set({
              accounts,
              strategies,
              setups,
              trades,
              expenses,
              payouts,
              dailyGoalTarget,
              dailyMaxLoss,
              breakEvenRange,
              selectedAccountIds,
              subscriptionStatus,
              trialEndsAt,
              budgetSettings,
              calendarPrefs,
              timezone,
            });
          } else {
            set({ subscriptionStatus, trialEndsAt, budgetSettings, timezone });
          }
        } catch (err) {
          console.error('[Store] reloadFromCloud failed:', err);
        } finally {
          set({ dataLoading: false });
        }
      },

      // ── Mentor read-only viewing ──────────────────────────────
      enterViewingMode: async (studentId, name) => {
        set({ viewingAs: { id: studentId, name }, dataLoading: true });
        try {
          const {
            accounts,
            strategies,
            trades,
            expenses,
            payouts,
            setups,
            dailyGoalTarget,
            dailyMaxLoss,
            breakEvenRange,
            calendarPrefs,
            timezone,
          } = await loadViewingData(studentId);
          set({
            accounts,
            strategies,
            setups,
            trades,
            expenses,
            payouts,
            dailyGoalTarget,
            dailyMaxLoss,
            breakEvenRange,
            calendarPrefs,
            timezone,
            selectedAccountIds: [],
          });
        } catch (err) {
          console.error('[Store] enterViewingMode failed:', err);
          set({ viewingAs: null });
        } finally {
          set({ dataLoading: false });
        }
      },

      exitViewingMode: async () => {
        set({ viewingAs: null });
        await get().reloadFromCloud();
      },

      getFilteredTrades: () => {
        const { trades, selectedAccountIds } = get();
        if (selectedAccountIds.length === 0) return trades;
        return trades.filter((t) => selectedAccountIds.includes(t.account_id));
      },

      getStats: () => {
        const trades = get().getFilteredTrades();
        const be = get().breakEvenRange;
        const { wins, losses } = splitByOutcome(trades, be);
        const totalPnL = trades.reduce((s, t) => s + t.pnl, 0);
        const avgWin = wins.length ? wins.reduce((s, t) => s + t.pnl, 0) / wins.length : 0;
        const avgLoss = losses.length ? losses.reduce((s, t) => s + t.pnl, 0) / losses.length : 0;
        const profitFactor =
          losses.length && avgLoss !== 0
            ? Math.abs((avgWin * wins.length) / (avgLoss * losses.length))
            : 0;
        const wlDenom = wins.length + losses.length;
        return {
          totalPnL,
          winRate: wlDenom ? (wins.length / wlDenom) * 100 : 0,
          totalTrades: trades.length,
          avgWin,
          avgLoss,
          profitFactor,
        };
      },
    }),
    {
      name: 'tradelog-storage',
      version: 6,
      migrate: (persisted: unknown, fromVersion: number) => {
        const state = persisted as Record<string, unknown>;
        const hasData = Array.isArray(state.accounts)
          ? (state.accounts as unknown[]).length > 0
          : Array.isArray(state.trades)
            ? (state.trades as unknown[]).length > 0
            : !!state.user;
        const onboardingDone = hasData ? true : (state.onboardingDone ?? false);
        // v5 → v6: introduce real dark mode; default light, keep prior choice only if explicitly false
        const darkMode = fromVersion >= 6 ? !!state.darkMode : false;
        return { ...state, darkMode, onboardingDone };
      },
      partialize: (s) => ({
        lang: s.lang,
        fontSize: s.fontSize,
        highContrast: s.highContrast,
        grayscale: s.grayscale,
        readableFont: s.readableFont,
        sidebarCollapsed: s.sidebarCollapsed,
        darkMode: s.darkMode,
        dailyGoalTarget: s.dailyGoalTarget,
        dailyMaxLoss: s.dailyMaxLoss,
        breakEvenRange: s.breakEvenRange,
        calendarPrefs: s.calendarPrefs,
        // שמור נתונים ב-localStorage רק כ-cache — Supabase הוא source of truth
        accounts: s.accounts,
        trades: s.trades,
        strategies: s.strategies,
        expenses: s.expenses,
        payouts: s.payouts,
        setups: s.setups,
        selectedAccountIds: s.selectedAccountIds,
        isDemo: s.isDemo,
        lastUserId: s.lastUserId,
        user: s.user,
        onboardingDone: s.onboardingDone,
      }),
    },
  ),
);

export function formatPnL(pnl: number): string {
  const abs = Math.abs(Math.round(pnl)).toLocaleString('en-US');
  return (pnl >= 0 ? '+' : '-') + '$' + abs;
}
